import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import authApi from '../api/auth';

function AnalyticsPage() {
  const { t } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalLogs: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  useEffect(() => {
    fetchLoginLogs();
  }, []);

  const fetchLoginLogs = async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      const response = await authApi.getLoginLogs(page, 10);
      setLogs(response.logs);
      setPagination(response.pagination);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getStatusIcon = (success) => {
    return success ? '✅' : '❌';
  };

  const getStatusText = (success) => {
    return success ? t('success') : t('failed');
  };

  const handlePageChange = (newPage) => {
    fetchLoginLogs(newPage);
  };

  const styles = {
    card: {
      backgroundColor: 'white',
      borderRadius: '10px',
      padding: '30px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    title: {
      textAlign: 'center',
      marginBottom: '30px',
      color: '#333',
      fontSize: '28px',
      fontWeight: 'bold',
    },
    subtitle: {
      textAlign: 'center',
      fontSize: '16px',
      color: '#666',
      marginBottom: '30px',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '20px',
    },
    tableHeader: {
      backgroundColor: '#f8f9fa',
      borderBottom: '2px solid #dee2e6',
    },
    tableHeaderCell: {
      padding: '12px 8px',
      textAlign: 'left',
      fontWeight: '600',
      color: '#495057',
      fontSize: '14px',
    },
    tableRow: {
      borderBottom: '1px solid #dee2e6',
    },
    tableRowHover: {
      backgroundColor: '#f8f9fa',
    },
    tableCell: {
      padding: '12px 8px',
      fontSize: '14px',
      color: '#212529',
    },
    statusCell: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    loading: {
      textAlign: 'center',
      padding: '40px',
      color: '#666',
      fontSize: '16px',
    },
    error: {
      textAlign: 'center',
      padding: '20px',
      color: '#dc3545',
      backgroundColor: '#f8d7da',
      borderRadius: '8px',
      marginBottom: '20px',
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '10px',
      marginTop: '30px',
    },
    paginationButton: {
      padding: '8px 16px',
      border: '1px solid #dee2e6',
      backgroundColor: 'white',
      color: '#495057',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      transition: 'all 0.2s ease',
    },
    paginationButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    paginationButtonActive: {
      backgroundColor: '#007bff',
      color: 'white',
      borderColor: '#007bff',
    },
    paginationInfo: {
      fontSize: '14px',
      color: '#666',
      margin: '0 20px',
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px',
      color: '#666',
      fontSize: '16px',
    },
  };

  if (loading) {
    return (
      <div style={styles.card}>
        <h1 style={styles.title}>{t('log')}</h1>
        <div style={styles.loading}>
          📊 {t('loading')}...
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <h1 style={styles.title}>{t('log')}</h1>
      <p style={styles.subtitle}>
        {t('activityLogs')} - {t('showing')} {logs.length} {t('of')} {pagination.totalLogs} {t('records')}
      </p>

      {error && (
        <div style={styles.error}>
          ❌ {error}
        </div>
      )}

      {logs.length === 0 ? (
        <div style={styles.emptyState}>
          📝 {t('noLoginLogs')}
        </div>
      ) : (
        <>
          <table style={styles.table}>
            <thead style={styles.tableHeader}>
              <tr>
                <th style={styles.tableHeaderCell}>{t('dateTime')}</th>
                <th style={styles.tableHeaderCell}>{t('status')}</th>
                <th style={styles.tableHeaderCell}>{t('ipAddress')}</th>
                <th style={styles.tableHeaderCell}>{t('userAgent')}</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, index) => (
                <tr 
                  key={log._id} 
                  style={{
                    ...styles.tableRow,
                    ...(index % 2 === 0 && styles.tableRowHover)
                  }}
                >
                  <td style={styles.tableCell}>
                    {formatDate(log.loginTime)}
                  </td>
                  <td style={styles.tableCell}>
                    <div style={styles.statusCell}>
                      <span>{getStatusIcon(log.success)}</span>
                      <span>{getStatusText(log.success)}</span>
                    </div>
                  </td>
                  <td style={styles.tableCell}>
                    {log.ipAddress}
                  </td>
                  <td style={styles.tableCell}>
                    <div style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.userAgent}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {pagination.totalPages > 1 && (
            <div style={styles.pagination}>
              <button
                style={{
                  ...styles.paginationButton,
                  ...(pagination.currentPage === 1 && styles.paginationButtonDisabled)
                }}
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
              >
                {t('previous')}
              </button>
              
              <span style={styles.paginationInfo}>
                {t('page')} {pagination.currentPage} {t('of')} {pagination.totalPages}
              </span>
              
              <button
                style={{
                  ...styles.paginationButton,
                  ...(pagination.currentPage === pagination.totalPages && styles.paginationButtonDisabled)
                }}
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
              >
                {t('next')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AnalyticsPage; 